import { BACKEND_API_HOST } from '@/app/config';

export type MeetingKind = 'lve' | 'previsit' | 'revisit' | 'other';
export type HostRole = 'estimator' | 'installer';

export interface MeetingContext {
  kind: MeetingKind;
  hostRole: HostRole;
}

export interface MeetingContextAnswer {
  // Null on an unknown kind or host role, so the copy falls back as a whole.
  context: MeetingContext | null;
  appointmentStart: string | null;
  isCancelled: boolean;
  currentMeetingUrl: string | null;
}

interface ContextBody {
  kind?: unknown;
  host_role?: unknown;
  appointment_start?: unknown;
  is_cancelled?: unknown;
  current_meeting_url?: unknown;
}

const KINDS: MeetingKind[] = ['lve', 'previsit', 'revisit', 'other'];
const HOST_ROLES: HostRole[] = ['estimator', 'installer'];

export const MEETING_CONTEXT_TIME_LIMIT_MS = 3000;

const known = <T extends string>(values: T[], value: unknown) => values.find(candidate => candidate === value);

const toContext = (body: ContextBody): MeetingContext | null => {
  const kind = known(KINDS, body.kind);
  const hostRole = known(HOST_ROLES, body.host_role);
  if (!kind || !hostRole) {
    return null;
  }

  return { kind, hostRole };
};

// A start that does not parse counts as none, so the lobby can still fall back
// to the one the meeting carries.
const startOf = (value: unknown) => (
  typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null
);

// The lobby sends the customer on to this link, so only one back into this app
// is accepted.
const sameOriginUrl = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }
  try {
    return new URL(value).origin === window.location.origin ? value : null;
  } catch {
    return null;
  }
};

const toAnswer = (body: ContextBody | null): MeetingContextAnswer | null => {
  if (!body) {
    return null;
  }

  return {
    context: toContext(body),
    appointmentStart: startOf(body.appointment_start),
    isCancelled: body.is_cancelled === true,
    currentMeetingUrl: sameOriginUrl(body.current_meeting_url),
  };
};

// Null is a final "no context" (404, unreadable body); a failure that may pass throws.
const fetchMeetingContext = async(meetingHash: string): Promise<MeetingContextAnswer | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MEETING_CONTEXT_TIME_LIMIT_MS);
  try {
    const response = await fetch(
      `${BACKEND_API_HOST}/api/schedule/video-meeting/${meetingHash}/context/`,
      { signal: controller.signal },
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Meeting context answered ${response.status}`);
    }
    return toAnswer(await response.json().catch(() => null));
  } finally {
    clearTimeout(timer);
  }
};

export default fetchMeetingContext;
